import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/AuthShell";
import { Button } from "@/components/ui/button";
import { LogOut, ArrowLeftRight } from "lucide-react";
import { formatApiError } from "@/lib/api";
import { isDesktopExperience, PREVIEW_DESKTOP_EVENT } from "@/lib/pwa";

const roleLabel = (r) => r === "AMMINISTRATORE" ? "Amministratore" : r === "UFFICIO" ? "Ufficio" : "Lavoratore";
const roleHome = (r) => r === "AMMINISTRATORE" ? "/admin" : r === "UFFICIO" ? "/ufficio" : "/lavoratore";

export function AppHeader() {
  const { user, logout, switchProfile } = useAuth();
  const navigate = useNavigate();
  const [switching, setSwitching] = useState(false);
  // In modalità Desktop l'header segue la piena larghezza del contenuto (allineamento logo/controlli ai bordi).
  const [desktop, setDesktop] = useState(() => isDesktopExperience());
  useEffect(() => {
    const r = () => setDesktop(isDesktopExperience());
    window.addEventListener(PREVIEW_DESKTOP_EVENT, r);
    window.addEventListener("appinstalled", r);
    let mq; try { mq = window.matchMedia("(display-mode: standalone)"); mq.addEventListener("change", r); } catch (_) {}
    return () => {
      window.removeEventListener(PREVIEW_DESKTOP_EVENT, r);
      window.removeEventListener("appinstalled", r);
      try { mq && mq.removeEventListener("change", r); } catch (_) {}
    };
  }, []);
  const initials = user ? `${user.nome?.[0] || ""}${user.cognome?.[0] || ""}`.toUpperCase() : "";

  const available = user?.available_profiles || [];
  const active = user?.active_profile || user?.profilo_accesso;
  const canSwitch = available.length >= 2;
  const target = canSwitch ? available.find((r) => r !== active) : null;

  const handleSwitch = async () => {
    if (!target || switching) return;
    setSwitching(true);
    try {
      await switchProfile(target);
      toast.success(`Ora stai operando come ${roleLabel(target)}`);
      navigate(roleHome(target), { replace: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSwitching(false);
    }
  };

  return (
    <header className="sticky top-0 z-40 w-full bg-white/85 backdrop-blur-md border-b border-slate-200">
      <div className={`${desktop ? "max-w-none px-4 md:px-6" : "max-w-5xl px-4"} mx-auto h-16 flex items-center justify-between`}>
        <Logo variant={active === "UFFICIO" ? "office" : "default"} />
        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
          {canSwitch && (
            <div className="flex items-center gap-2 min-w-0" data-testid="profile-switcher">
              <span className={`hidden sm:inline-flex items-center rounded-full text-xs font-semibold px-3 py-1.5 shadow-sm shrink-0 ${active === "UFFICIO" ? "bg-[#F0E1E7] border border-[#D8B4C6] text-[#8A2F52]" : "bg-[#E3EDF5] border border-[#8FB2CC] text-[#2F5A7A]"}`}
                data-testid="active-profile-indicator">
                Profilo {roleLabel(active)}
              </span>
              <Button variant="outline" size="sm" onClick={handleSwitch} disabled={switching}
                data-testid="switch-profile-btn" className="rounded-full h-8 text-xs font-medium shrink-0">
                <ArrowLeftRight className="h-3.5 w-3.5 mr-1.5 shrink-0" />
                {switching ? "Cambio…" : (
                  <>
                    <span className="sm:hidden">{roleLabel(target)}</span>
                    <span className="hidden sm:inline">Passa al profilo {roleLabel(target)}</span>
                  </>
                )}
              </Button>
            </div>
          )}
          <div className="text-right hidden sm:block">
            <p className="text-sm font-semibold text-slate-800 leading-tight">{user?.nome} {user?.cognome}</p>
            <p className="text-xs text-slate-500">{roleLabel(active)}</p>
          </div>
          <div className="h-9 w-9 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm font-bold font-heading">
            {initials}
          </div>
          <Button variant="ghost" size="icon" onClick={logout} data-testid="logout-btn"
            className="text-slate-500 hover:text-destructive">
            <LogOut className="h-5 w-5" />
          </Button>
        </div>
      </div>
    </header>
  );
}
